import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EnvelopeIcon } from '@heroicons/react/24/outline';

import useAuth from '../hooks/useAuth';
import LoadingSpinner from '../components/core/LoadingSpinner';
import { AuthLayout, GoogleSignInButton, MicrosoftSignInButton, OrDivider, PasswordInput } from '../components/auth';
import { resendVerification, OAuthProviderId } from '../services/emailAuthService';
import { startOAuth as beginOAuth } from '../utils/oauthUtils';

/**
 * SignupContinue - the page after entering an email on /signup.
 *
 * On load it registers the email and shows one of:
 * - Existing Google/Microsoft account → prompt to sign in with that provider
 * - Existing password account → password field to sign in
 * - New/incomplete signup → "verification email sent" with a resend button;
 *   clicking the emailed link signs the user in and starts onboarding
 *   (whose first step collects name + password).
 */

type ContinueStage = 'checking' | 'check-email' | 'oauth-exists' | 'password-exists' | 'failed';

const SignupContinue: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { register, login, error: authError, clearError } = useAuth();

  const email: string = (location.state as any)?.email || '';

  const [stage, setStage] = useState<ContinueStage>('checking');
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [providers, setProviders] = useState<OAuthProviderId[]>([]);
  const [signingIn, setSigningIn] = useState(false);
  const [loadingProvider, setLoadingProvider] = useState<'google' | 'microsoft' | null>(null);
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>('idle');
  // Guard against double registration (React StrictMode double-invoke)
  const startedRef = useRef(false);

  useEffect(() => {
    if (!email || startedRef.current) return;
    startedRef.current = true;

    const run = async () => {
      clearError();
      const result = await register(email);
      if (result.success && result.requiresVerification) {
        setStage('check-email');
      } else if (result.success && result.existingAccount === 'oauth') {
        setProviders(result.providers || []);
        setStage('oauth-exists');
      } else if (result.success && result.existingAccount === 'password') {
        // Providers may also be linked — offered as a secondary option below
        setProviders(result.providers || []);
        setStage('password-exists');
      } else {
        setStage('failed');
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  // Surface auth context errors (login failures) in the layout error slot
  useEffect(() => {
    if (authError) setError(authError);
  }, [authError]);

  // Opened directly without an email — go collect one
  if (!email) {
    return <Navigate to="/signup" replace />;
  }

  const providerName = (p: OAuthProviderId) => (p === 'google' ? 'Google' : 'Microsoft');

  const handlePasswordSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || signingIn) return;
    setError(null);
    setSigningIn(true);
    try {
      await login(email, password);
    } finally {
      setSigningIn(false);
    }
  };

  const handleResendVerification = async () => {
    if (resendState !== 'idle') return;
    setResendState('sending');
    try {
      await resendVerification(email);
    } finally {
      setResendState('sent');
    }
  };

  const startOAuth = (provider: 'google' | 'microsoft') => {
    setError(null);
    setLoadingProvider(provider);
    try {
      // login mode: account exists, so account picker only (no consent re-run)
      beginOAuth(provider, 'login');
    } catch (err) {
      setError(t('common:errors.connectionFailed', 'Failed to connect. Please try again.'));
      setLoadingProvider(null);
    }
  };

  const changeEmailLink = (
    <button
      type="button"
      onClick={() => navigate('/signup', { state: { email } })}
      className="text-sm font-medium text-primary hover:text-primary-700"
    >
      {t('common:auth.useDifferentEmail', 'Use a different email')}
    </button>
  );

  const emailChip = (
    <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-gray-50">
      <span className="text-sm text-gray-900 truncate">{email}</span>
      {changeEmailLink}
    </div>
  );

  if (stage === 'checking') {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center">
        <LoadingSpinner size="xl" color="primary" />
        <h2 className="mt-4 text-lg font-medium text-gray-900">
          {t('common:auth.checkingEmail', 'Checking...')}
        </h2>
      </div>
    );
  }

  if (stage === 'check-email') {
    return (
      <AuthLayout
        title={t('common:auth.checkYourEmail', 'Check your email')}
        subtitle={t('common:auth.checkYourEmailSubtitle', 'We sent you a verification link to activate your account')}
        mode="signup"
        isInAppBrowser={false}
      >
        <div className="flex justify-center mb-2">
          <div className="p-3 bg-primary-50 rounded-full">
            <EnvelopeIcon className="h-10 w-10 text-primary" />
          </div>
        </div>
        <p className="text-sm text-gray-600 text-center">
          {t('common:auth.verificationSentTo', 'We sent a verification link to')}{' '}
          <span className="font-medium text-gray-900">{email}</span>.{' '}
          {t('common:auth.verificationClickToContinue', 'Click the link in the email to continue setting up your account.')}
        </p>
        <div className="text-center">
          <button
            type="button"
            onClick={handleResendVerification}
            disabled={resendState !== 'idle'}
            className="text-sm font-medium text-primary hover:text-primary-700 disabled:text-gray-400"
          >
            {resendState === 'sent'
              ? t('common:auth.verificationEmailSent', 'Verification email sent!')
              : resendState === 'sending'
              ? t('common:auth.sendingVerification', 'Sending...')
              : t('common:auth.resendVerification', 'Resend verification email')}
          </button>
        </div>
        <div className="text-center">{changeEmailLink}</div>
      </AuthLayout>
    );
  }

  if (stage === 'failed') {
    return (
      <AuthLayout
        title={t('common:auth.getStartedFree', 'Get started for free')}
        subtitle={t('common:auth.signUpSubtitle', 'Create your account and start scheduling in minutes')}
        mode="signup"
        error={error || t('common:auth.signupFailed', 'Something went wrong. Please try again.')}
      >
        <div className="text-center">
          <Link to="/signup" state={{ email }} className="font-medium text-primary hover:text-primary-700">
            {t('common:actions.tryAgain', 'Try again')}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('common:auth.welcomeBack', 'Welcome back')}
      subtitle={t('common:auth.signInSubtitle', 'Let\'s get your scheduling off to a smooth start')}
      mode="signup"
      error={error}
    >
      {stage === 'oauth-exists' && (
        <div className="space-y-4">
          {emailChip}
          <div className="bg-primary-50 border-l-4 border-primary p-4 rounded-r">
            <p className="text-sm text-primary-700">
              {t('common:auth.accountExistsOauth', 'You already have an account with this email — it signs in with {{provider}}. Continue below.', {
                provider: providers.map(providerName).join(' / '),
              })}
            </p>
          </div>
          {providers.includes('google') && (
            <GoogleSignInButton
              onClick={(e) => { e.preventDefault(); startOAuth('google'); }}
              isLoading={loadingProvider === 'google'}
              disabled={loadingProvider !== null}
              mode="signin"
            />
          )}
          {providers.includes('microsoft') && (
            <MicrosoftSignInButton
              onClick={(e) => { e.preventDefault(); startOAuth('microsoft'); }}
              isLoading={loadingProvider === 'microsoft'}
              disabled={loadingProvider !== null}
              mode="signin"
            />
          )}
        </div>
      )}

      {stage === 'password-exists' && (
        <>
        <form onSubmit={handlePasswordSignIn} className="space-y-4" noValidate>
          {emailChip}
          <div className="bg-primary-50 border-l-4 border-primary p-4 rounded-r">
            <p className="text-sm text-primary-700">
              {t('common:auth.accountExistsPassword', 'You already have an account with this email. Enter your password to sign in.')}
            </p>
          </div>
          <PasswordInput
            id="signup-existing-password"
            value={password}
            onChange={setPassword}
            label={t('common:auth.passwordLabel', 'Password')}
            autoComplete="current-password"
            disabled={signingIn}
            labelAction={
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-primary hover:text-primary-700"
              >
                {t('common:auth.forgotPassword', 'Forgot your password?')}
              </Link>
            }
          />
          <button type="submit" className="btn-primary btn-lg w-full" disabled={signingIn || !password}>
            {signingIn
              ? t('common:auth.signingIn', 'Signing in...')
              : t('common:auth.signIn', 'Sign In')}
          </button>
        </form>

        {providers.length > 0 && (
          <>
            <OrDivider />
            {providers.includes('google') && (
              <GoogleSignInButton
                onClick={(e) => { e.preventDefault(); startOAuth('google'); }}
                isLoading={loadingProvider === 'google'}
                disabled={loadingProvider !== null || signingIn}
                mode="signin"
              />
            )}
            {providers.includes('microsoft') && (
              <MicrosoftSignInButton
                onClick={(e) => { e.preventDefault(); startOAuth('microsoft'); }}
                isLoading={loadingProvider === 'microsoft'}
                disabled={loadingProvider !== null || signingIn}
                mode="signin"
              />
            )}
          </>
        )}
        </>
      )}
    </AuthLayout>
  );
};

export default SignupContinue;
